import { ContextMenu, ContextMenuItem } from '@bp/ui-components';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';
import { CellItem, RoomItem } from '../../../stores/TimetableVersion/PinboardStoreTypes';
import { isPlaced } from '../../../stores/TimetableVersion/SubjectContainerTypes';
import { getSubjectCardTextColor } from '../../../utils/getCardTextColor';
import { getConflictingCardLabel } from '../../../utils/getConflictingCardLabel';
import { getWarningTypeTranslation } from '../../../utils/getWarningTypeTranslation';
import { getRoomLabelWithBuilding } from '../../../utils/roomLabel';
import { CardType } from '../../TimetableVersion/graphql/types';
import { useTimetableStore } from '../TimetableProvider';
import { CardIcons } from './CardIcons/CardIcons';
import styles from './GridCard.module.scss';

type GridCardProps = {
  card: CardType;
  width: string;
  height: string;
  top: string;
  background: string;
  subjectColor?: string;
  textColor: string;
  left: string;
  cell: CellItem;
};

export const GridCard = observer(
  ({ card, top, width, background, subjectColor, height, textColor, left, cell }: GridCardProps) => {
    const store = useTimetableStore();
    const { t } = useTranslation();

    const cardConflicts = store.cardConflicts.get(card.uuid);

    let hasConflicts = false;
    let hasMaybe = false;

    cardConflicts?.forEach((conflict) => {
      if (!conflict.maybe) {
        hasConflicts = true;
      } else {
        hasMaybe = true;
      }
      if (cell.row.isFunctionClass && (conflict.type === 'class' || conflict.type === 'divisionGroup')) {
        hasConflicts = false;
        hasMaybe = false;
      }

      // ignore conflicts if all cards from the same subject container
      if ((card.isSubjectContainer && conflict.type === 'teacher') || conflict.type === 'room') {
        let allCardsFromSameSubjectContainer = true;
        conflict.placed.forEach((uuid) => {
          const _card = store.getCardRaw(uuid);
          if (
            _card?.isSubjectContainer &&
            _card.subject?.uuid === card.subject?.uuid &&
            allCardsFromSameSubjectContainer
          ) {
            allCardsFromSameSubjectContainer = true;
          } else {
            allCardsFromSameSubjectContainer = false;
          }
        });
        if (allCardsFromSameSubjectContainer && !conflict.blocked && !conflict.maybe) {
          hasConflicts = false;
        }
      }
    });

    const _cardRooms = store.cardRooms.get(card.uuid);

    const noRooms = !_cardRooms || _cardRooms?.size === 0;

    const cardSearchText = useMemo(
      () =>
        [
          card.subject?.name,
          card.subject?.shortName,
          ...card.teachers.map((t) => `${t.firstName} ${t.lastName} ${t.shortName}`),
          ...card.classes.map((c) => c.shortName),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase(),
      [card.subject?.name, card.subject?.shortName, card.teachers, card.classes],
    );

    const menuClasses = classNames(styles.menu);

    const cardClasses = classNames(styles['grid-card'], {
      [styles.pinned]: store.pinnedCard === card.uuid,
      [styles.locked]: store.lockedCards.has(card.uuid),
      [styles['low-light']]:
        (store.controlMode.isActive || store.timefoldAnalyzeMode.isActive) && !store.findingHighlights.has(card.uuid),
      // positives Spotlight: gehighlightete Karten heben sich auch ab, wenn wenig gedimmt ist
      [styles['high-light']]:
        (store.controlMode.isActive || store.timefoldAnalyzeMode.isActive) && store.findingHighlights.has(card.uuid),
    });

    const [menuOpen, setMenuOpen] = useState(false);

    // Ref holds the latest menu builder — always a fresh closure, never stale
    const buildMenuRef = useRef<() => ContextMenuItem[]>(() => []);
    buildMenuRef.current = () => {
      const overlappingCards = store.getOverlappingCards(card.uuid);
      const groupedRooms = store.defaultCardRooms.get(card.uuid) ?? {
        lessonRoomItems: [],
        subjectRoomItems: [],
        classRoomItems: [],
        teacherRoomItems: [],
      };

      const onOverlappingCardClick = (event: React.MouseEvent<HTMLDivElement>, overlappingCard: CardType) => {
        event.stopPropagation();
        store.setHoveredCard(overlappingCard.uuid);
        store.pickCard(
          {
            cardUuid: overlappingCard.uuid,
            fromStack: false,
            label: overlappingCard.subject?.shortName ?? '',
            counter: 1,
            labelColor: overlappingCard.badgeCardTextColor,
            duration: overlappingCard.duration ?? 1,
            cardRows: overlappingCard.badgeCardRows,
            position: { x: event.clientX, y: event.clientY },
          },
          {
            day: cell.day,
            hour: cell.hour,
            row: toJS(cell.row),
          },
          false,
          event.shiftKey,
        );
      };

      const currentRoomsUuids = _cardRooms ?? new Set<string>();

      const hasRooms = currentRoomsUuids.size > 0;

      const menu: ContextMenuItem[] = [];
      menu.push({
        type: 'component',
        node: <div className={styles['menu-name']}>{card?.subject?.name}</div>,
      });

      if (hasConflicts || !hasRooms || hasMaybe) {
        menu.push({
          type: 'ruler',
        });
      }

      if (!hasRooms) {
        menu.push({
          type: 'component',
          node: <div className={styles['menu-info']}>{t('pinboard.reason.noRoom')}</div>,
        });
      }

      const conflicts: ReactNode[] = [];
      const maybe: ReactNode[] = [];

      const fmt = (d: string) => dayjs(d).format('DD.MM.');
      const getEpochRanges = (resourceUuid: string, type: 'teacher' | 'room', placedUuids: string[]): string[] => {
        if (!store.showEpochRanges) return [];
        const ranges: string[] = [];
        placedUuids.forEach((uid) => {
          store.subjectContainerSlots
            .get(uid)
            ?.filter(isPlaced)
            .filter((s) => (type === 'teacher' ? s.teacherUuids : s.roomUuids).includes(resourceUuid))
            .forEach((s) => ranges.push(`${fmt(s.startDate)} – ${fmt(s.endDate)}`));
        });
        if (ranges.length === 0) {
          store.subjectContainerSlots
            .get(card.uuid)
            ?.filter(isPlaced)
            .filter((s) => (type === 'teacher' ? s.teacherUuids : s.roomUuids).includes(resourceUuid))
            .forEach((s) => ranges.push(`${fmt(s.startDate)} – ${fmt(s.endDate)}`));
        }
        return ranges;
      };

      cardConflicts?.forEach((conflict, key) => {
        const placedWithoutCurrent = Array.from(conflict.placed).filter((uuid) => uuid !== card.uuid);

        if (conflict.type === 'class' && conflict.maybe) {
          maybe.push(
            <>
              {getWarningTypeTranslation('classMaybe')} ({store.getNameForUuid(key, 'class')})
            </>,
          );
        }
        if (conflict.type === 'teacher' && conflict.maybe) {
          maybe.push(
            <>
              {getWarningTypeTranslation('teacherMaybe')} ({store.getNameForUuid(key, 'teacher')})
            </>,
          );
        }
        if (conflict.type === 'room' && conflict.maybe) {
          maybe.push(
            <>
              {getWarningTypeTranslation('roomMaybe')} ({store.getNameForUuid(key, 'room')})
            </>,
          );
        }
        if (conflict.type === 'subject' && conflict.maybe) {
          maybe.push(
            <>
              {getWarningTypeTranslation('subjectMaybe')} ({store.getNameForUuid(key, 'subject')})
            </>,
          );
        }
        if (conflict.type === 'subject' && conflict.blocked) {
          conflicts.push(<>{getWarningTypeTranslation('subjectNotAvailable')}</>);
        }
        if (conflict.type === 'class' && conflict.blocked) {
          conflicts.push(
            <>
              {getWarningTypeTranslation('classNotAvailable')} ({store.getNameForUuid(key, 'class')})
            </>,
          );
        }
        if (conflict.type === 'class' && !conflict.blocked && !conflict.maybe && placedWithoutCurrent.length > 0) {
          const conflictingLabel = getConflictingCardLabel(placedWithoutCurrent, (uuid) => store.getCardByUuid(uuid));
          conflicts.push(
            <>
              {getWarningTypeTranslation('overlappingClass')} ({store.getNameForUuid(key, 'class')})
              {conflictingLabel && <div className={styles['epoch-range']}>{conflictingLabel}</div>}
            </>,
          );
        }
        if (conflict.type === 'teacher' && conflict.blocked) {
          conflicts.push(
            <>
              {getWarningTypeTranslation('teacherNotAvailable')} ({store.getNameForUuid(key, 'teacher')})
            </>,
          );
        }
        if (conflict.type === 'teacher' && !conflict.blocked && !conflict.maybe && placedWithoutCurrent.length > 0) {
          const epochRanges = getEpochRanges(key, 'teacher', placedWithoutCurrent);
          const conflictingLabel = getConflictingCardLabel(placedWithoutCurrent, (uuid) => store.getCardByUuid(uuid));
          conflicts.push(
            <>
              {getWarningTypeTranslation('overlappingTeachers')} ({store.getNameForUuid(key, 'teacher')})
              {epochRanges.length > 0 && <div className={styles['epoch-range']}>{epochRanges.join(', ')}</div>}
              {conflictingLabel && <div className={styles['epoch-range']}>{conflictingLabel}</div>}
            </>,
          );
        }
        if (conflict.type === 'room' && conflict.blocked) {
          conflicts.push(
            <>
              {getWarningTypeTranslation('roomNotAvailable')} ({store.getNameForUuid(key, 'room')})
            </>,
          );
        }
        if (conflict.type === 'room' && !conflict.blocked && !conflict.maybe && placedWithoutCurrent.length > 0) {
          const epochRanges = getEpochRanges(key, 'room', placedWithoutCurrent);
          const conflictingLabel = getConflictingCardLabel(placedWithoutCurrent, (uuid) => store.getCardByUuid(uuid));
          conflicts.push(
            <>
              {getWarningTypeTranslation('overlappingRooms')} ({store.getNameForUuid(key, 'room')})
              {epochRanges.length > 0 && <div className={styles['epoch-range']}>{epochRanges.join(', ')}</div>}
              {conflictingLabel && <div className={styles['epoch-range']}>{conflictingLabel}</div>}
            </>,
          );
        }
        if (
          conflict.type === 'divisionGroup' &&
          !conflict.blocked &&
          !conflict.maybe &&
          placedWithoutCurrent.length > 0
        ) {
          const conflictingLabel = getConflictingCardLabel(placedWithoutCurrent, (uuid) => store.getCardByUuid(uuid));
          conflicts.push(
            <>
              {getWarningTypeTranslation('notMatchingDivision')} ({store.getNameForUuid(key, 'divisionGroup')})
              {conflictingLabel && <div className={styles['epoch-range']}>{conflictingLabel}</div>}
            </>,
          );
        }
      });

      if (hasConflicts) {
        menu.push({
          type: 'component',
          node: (
            <div className={styles['menu-warning']}>
              {conflicts?.map((conflict, index) => {
                return (
                  <div key={index} className={styles['menu-warning']}>
                    {conflict}
                  </div>
                );
              })}
            </div>
          ),
        });
      }
      if (hasMaybe) {
        menu.push(
          {
            type: 'ruler',
          },
          {
            type: 'component',
            node: (
              <div className={styles['menu-info']}>
                {maybe?.map((conflict, index) => {
                  return (
                    <div key={index} className={styles['menu-info']}>
                      {conflict}
                    </div>
                  );
                })}
              </div>
            ),
          },
        );
      }

      menu.push(
        {
          type: 'ruler',
        },
        {
          label: store.pinnedCard === card?.uuid ? t('pinboard.actions.unpin') : t('pinboard.actions.pin'),
          onClick: (e) => {
            e.stopPropagation();
            if (card) {
              if (store.pinnedCard === card?.uuid) {
                store.pinnedCard = null;
                store.setPinnedCard(null);
              } else {
                store.setPinnedCard(card?.uuid ?? null);
              }
            }
          },
        },
        {
          disabled: store.context === 'rooms',
          label: store.lockedCards.has(card?.uuid ?? '') ? t('pinboard.actions.unlock') : t('pinboard.actions.lock'),
          onClick: () => {
            if (card) {
              if (store.lockedCards.has(card?.uuid ?? '')) {
                store.unlockCard(card?.uuid ?? '');
              } else {
                store.lockCard(card?.uuid ?? '');
              }
            }
          },
        },
        {
          type: 'ruler',
        },
        {
          // Epochenschienen-Karten bekommen ihre Räume aus dem Epochenplan und dürfen keine
          // eigenen zugewiesen bekommen (siehe canHaveRooms im CardStore).
          disabled: card.isSubjectContainer,
          tooltip: card.isSubjectContainer ? t('pinboard.actions.noRoomsForSubjectContainerHint') : undefined,
          label: t('rooms.title', { count: 2 }),
          subContent: card.isSubjectContainer ? [] : getRoomsSubmenu(),
        },
        {
          label: t('pinboard.actions.singleHour'),
          tooltip: t('pinboard.actions.noSingleHourHint'),
          disabled: true,
        },
      );

      if (overlappingCards.length > 0) {
        menu.push({
          type: 'ruler',
        });
        overlappingCards.forEach((overlappingCard) => {
          const gridCards = store.gridCards.get(overlappingCard.uuid) ?? [];
          menu.push({
            type: 'component',
            node: (
              <div
                className={styles['grid-card-overlapping-click-wrapper']}
                onClick={(e) => {
                  onOverlappingCardClick(e, overlappingCard);
                }}
              >
                <div className={styles['grid-card-overlapping']}>
                  <div
                    onClick={(e) => {
                      onOverlappingCardClick(e, overlappingCard);
                    }}
                    title={overlappingCard.subject?.name}
                    className={styles['grid-card']}
                    style={{
                      height: gridCards[0].cssHeight ?? 0,
                      width: `calc(${(card.duration ?? 1) * 100}%`,
                      top: gridCards[0].cssTop ?? '0%',
                      background: gridCards[0].cssGradientString ?? '',
                      color: gridCards[0].textColor ?? '',
                      left: '0%',
                    }}
                  >
                    <div className={styles.title}>{overlappingCard.subject?.shortName}</div>

                    <div className={styles.info}>
                      <CardIcons cardUuid={toJS(overlappingCard.uuid)} />
                    </div>
                  </div>
                </div>
              </div>
            ),
          });
        });
      }

      function getRoomsSubmenu(): ContextMenuItem[] {
        const roomsSubmenu: ContextMenuItem[] = [];

        const pushRoomSwitch = (room: RoomItem) => {
          roomsSubmenu.push({
            value: currentRoomsUuids.has(room.value),
            className: room.inUse ? styles['menu-room-used'] : undefined,
            onValueChange: (value) => onRoomValueChange(value as boolean, room),
            label: getRoomLabelWithBuilding(room.label, room.buildingName),
            type: 'switch',
          });
        };

        // 1. Tatsächlich zugeordnete Räume (aktuell an der Karte), danach ein Trenner.
        const assignedRooms = Array.from(_cardRooms?.values() ?? []).filter((room) =>
          currentRoomsUuids.has(room.value),
        );
        if (assignedRooms.length > 0) {
          roomsSubmenu.push({ type: 'header', label: t('rooms.title.assigned') });
          assignedRooms.forEach(pushRoomSwitch);
          roomsSubmenu.push({ type: 'ruler' });
        }

        // 2. Räume aus den Vorräten, in Hierarchie-Reihenfolge, jede Gruppe mit eigener
        //    Zwischenüberschrift: Raumvorrat (lesson) > Fachraum (subject) > Lehrerraum (teacher) > Klassenraum (class).
        const roomGroups: { label: string; items: RoomItem[] }[] = [
          { label: t('rooms.title.lesson'), items: groupedRooms.lessonRoomItems },
          { label: t('rooms.title.subject'), items: groupedRooms.subjectRoomItems },
          { label: t('rooms.title.teacher'), items: groupedRooms.teacherRoomItems },
          { label: t('rooms.title.class'), items: groupedRooms.classRoomItems },
        ];

        roomGroups.forEach(({ label, items }) => {
          const available = items.filter((room) => !currentRoomsUuids.has(room.value));
          if (available.length === 0) return;
          roomsSubmenu.push({ type: 'header', label });
          available.forEach(pushRoomSwitch);
        });

        if (roomsSubmenu.length === 0) {
          roomsSubmenu.push({});
        }

        function onRoomValueChange(value: boolean, room: RoomItem) {
          if (value) {
            store.setRoomsForCards([card], [room], true);
          } else {
            store.removeRoomsFromCards([card], [room]);
          }
        }
        return roomsSubmenu;
      }

      return menu;
    };

    const onCardClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        e.preventDefault();
        store.setHoveredCard(card.uuid);

        if (store.pickedCard) {
          store.placeCard(cell);
        } else if (card) {
          store.pickCard(
            {
              cardUuid: card.uuid,
              fromStack: false,
              label: card.subject?.shortName ?? '',
              counter: 1,
              labelColor: card.badgeCardTextColor,
              duration: card.duration ?? 1,
              cardRows: card.badgeCardRows,
              position: { x: e.clientX, y: e.clientY },
            },
            {
              day: cell.day,
              hour: cell.hour,
              row: toJS(cell.row),
            },
            false,
            e.shiftKey,
          );
        }
      },
      [card, cell, store],
    );

    const debounceOnCardHover = useDebounceCallback(() => {
      store.setHoveredCard(card.uuid);
    }, 500);

    const onMouseEnter = useCallback(() => {
      if (!store.hoveredCard) {
        store.setHoveredCard(card.uuid);
      } else {
        debounceOnCardHover();
      }
    }, [card.uuid, debounceOnCardHover, store]);

    const onMouseLeave = useCallback(() => {
      debounceOnCardHover.cancel();
    }, [debounceOnCardHover]);

    const onOpenChange = useCallback(
      (open: boolean) => {
        setMenuOpen(open);
        debounceOnCardHover.cancel();
        store.setHoveredCard(card.uuid);
      },
      [card.uuid, debounceOnCardHover, store],
    );

    // Menü während des Renders bauen (nur wenn offen), damit die im Builder gelesenen Observables
    // (z.B. cardRooms) von MobX getrackt werden und die Switches beim Toggeln reaktiv aktualisieren.
    // Geschlossen bleibt es leer -> kein Build/Tracking-Overhead pro Karte.
    const contextMenuItems = menuOpen ? buildMenuRef.current() : [];

    const titleChunks = [];
    if (card.lesson?.subTitle) {
      titleChunks.push(card.lesson.subTitle);
    }
    if (card.subject?.name) {
      titleChunks.push(card.subject.name);
    }
    if (card.teachers.length > 0) {
      titleChunks.push(card.teachers.map((t) => t.shortName).join(', '));
    }
    if (card.classes.length > 0) {
      titleChunks.push(card.classes.map((c) => c.shortName).join(', '));
    }
    const baseTitle = titleChunks.join(' | ');

    // Darstellung: LuL-Kürzel statt Fach-Kürzel auf dem Kärtchen (nichts anzeigen, wenn keine Lehrkraft).
    const teacherShortNames = card.teachers
      .map((t) => t.shortName)
      .filter(Boolean)
      .join(', ');
    const cardLabel = store.showTeacherInsteadOfSubject ? teacherShortNames : card.subject?.shortName;

    return (
      <ContextMenu className={menuClasses} data={contextMenuItems} usePortal={false} onOpenChange={onOpenChange}>
        <div
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={onCardClick}
          title={baseTitle}
          className={cardClasses}
          data-card-uuid={card.uuid}
          data-no-rooms={noRooms && card.startTimeGridEntry ? '' : undefined}
          data-has-conflict={hasConflicts ? '' : undefined}
          data-search-text={cardSearchText}
          data-function-card={
            card.classes.length === 0 || card.classes.some((c) => c.grade === null) ? 'true' : 'false'
          }
          style={
            {
              '--grid-card-bg': background,
              '--grid-card-subject-bg': subjectColor,
              '--grid-card-text': textColor,
              // Beide Textfarben werden vorberechnet; welche gilt, entscheidet CSS über
              // `data-color-by-subject` am Board-Container. Kein Store-Abo je Kärtchen.
              '--grid-card-subject-text': getSubjectCardTextColor(subjectColor),
              height: hasConflicts ? `calc(${height} - 4px)` : height,
              width: hasConflicts ? `calc(${width} - 4px)` : width,
              top: hasConflicts ? `calc(${top} + 2px)` : top,
              left: hasConflicts ? `calc(${left} + 2px)` : left,
            } as React.CSSProperties
          }
        >
          <div className={styles.title}>{cardLabel}</div>

          <div className={styles.info}>
            <CardIcons cardUuid={toJS(card.uuid)} />
          </div>
        </div>
      </ContextMenu>
    );
  },
);
GridCard.displayName = 'GridCard';
